// Image modal component for displaying full-size product images
import React, { useEffect } from 'react';
import { Product } from '../types';
import { useCartStore } from '../store/cartStore';
import { calculateDisplayPrice } from '../shared/pricing';
import './ImageModal.css';

interface ImageModalProps {
    isOpen: boolean;
    imageUrl: string;
    imageName: string;
    product?: Product; // Optional full product details
    onClose: () => void;
    onAddToCart?: () => void; // Callback to open add to cart modal
}

const ImageModal: React.FC<ImageModalProps> = ({ isOpen, imageUrl, imageName, product, onClose, onAddToCart }) => {
    const { cart } = useCartStore();

    const handleAddToCart = () => {
        onClose();
        if (onAddToCart) {
            onAddToCart();
        }
    };

    // Get discount percentage from cart
    const discountPercentage = cart.discount?.percentage;
    const priceDisplay = product ? calculateDisplayPrice(product.price, discountPercentage) : null;
    // Handle Escape key to close modal
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            // Prevent background scrolling when modal is open
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="image-modal-overlay" onClick={onClose}>
            <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="image-modal-close" onClick={onClose} aria-label="Close">
                    ✕
                </button>
                <div className="image-modal-body">
                    <div className="image-modal-wrapper">
                        <img
                            src={imageUrl}
                            alt={imageName}
                            className="image-modal-image"
                        />
                    </div>
                    <div className="image-modal-details">
                        <div className="image-modal-caption">
                            <h3>{imageName}</h3>
                        </div>

                        {/* Campaign Badge */}
                        {product?.campaignText && (
                            <div className="campaign-badge-image-modal">
                                {product.campaignText}
                            </div>
                        )}

                        {/* Price Display - Always shown */}
                        {product && priceDisplay && (
                            <div className="price-display-image-modal">
                                {priceDisplay.hasDiscount && priceDisplay.originalPrice && (
                                    <span className="original-price-image-modal">
                                        £{priceDisplay.originalPrice.toFixed(2)}
                                    </span>
                                )}
                                <span className="current-price-image-modal">
                                    £{priceDisplay.currentPrice.toFixed(2)}
                                </span>
                            </div>
                        )}

                        {/* Add to Cart Button */}
                        {product && onAddToCart && (
                            <button className="add-to-cart-btn-modal" onClick={handleAddToCart}>
                                Add to Cart
                            </button>
                        )}

                        {/* Product Description */}
                        {product?.description && (
                            <p className="product-description-modal">{product.description}</p>
                        )}

                        {/* Detailed Description */}
                        {product?.detailedDescription && (
                            <div className="product-detailed-modal">
                                <h4>Details</h4>
                                <p>{product.detailedDescription}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImageModal;
